/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useId, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { cssMap, jsx } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';

export type HoverCardProps = {
	content: ReactNode;
	children: ReactNode;
	placement?: React.ComponentProps<typeof Popup>['placement'];
	fallbackPlacements?: React.ComponentProps<typeof Popup>['fallbackPlacements'];
	disabled?: boolean;
	autoFocus?: boolean;
	testId?: string;
	hoverCardDelay?: number;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
};

const hovercardStyles = cssMap({
	trigger: {
		width: '100%',
	},
});

export const HoverCard = ({
	content,
	children,
	placement = 'left',
	autoFocus = false,
	fallbackPlacements = ['bottom', 'left', 'right'],
	disabled = false,
	hoverCardDelay = 200,
	testId,
	onMouseEnter,
	onMouseLeave,
}: HoverCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [setIsOpenDebounced, cancelSetIsOpen] = useDebouncedCallback((open: boolean) => {
		setIsOpen(open);
	}, hoverCardDelay);
	const contentId = useId();

	if (disabled) {
		return children;
	}

	return (
		<Popup
			placement={placement}
			fallbackPlacements={fallbackPlacements}
			autoFocus={autoFocus}
			isOpen={isOpen}
			content={() => (
				<Box
					id={contentId}
					as="span"
					onMouseEnter={() => cancelSetIsOpen()}
					onMouseLeave={() => setIsOpenDebounced(false)}
					testId={testId ? `${testId}--content` : undefined}
				>
					{content}
				</Box>
			)}
			trigger={(triggerProps) => {
				// Filter out aria-expanded - it's invalid on non-interactive elements like <span>
				// aria-expanded is only valid on interactive elements (e.g., button, menuitem)
				// Use aria-describedby instead to link the trigger to the content for screen readers
				const { 'aria-expanded': _ariaExpanded, ...restTriggerProps } = triggerProps;

				return (
					<Box
						as="span"
						{...restTriggerProps}
						aria-describedby={contentId}
						testId={testId ? `${testId}--trigger` : undefined}
						xcss={hovercardStyles.trigger}
						onMouseEnter={() => {
							setIsOpenDebounced(true);
							onMouseEnter?.();
						}}
						onMouseLeave={() => {
							setIsOpenDebounced(false);
							onMouseLeave?.();
						}}
					>
						{children}
					</Box>
				);
			}}
		/>
	);
};
